import { createContext, useContext, useEffect, useState, type ReactElement } from 'react';
import { Modal } from './Modal';
import { useT } from '../lib/i18n';
import type { Dictionary } from '../content/pt-BR';

export type ChaveDeAjuda = keyof Dictionary['ajuda']['telas'];

/**
 * Qual tutorial a tela aberta usa. A rota já resolve a maioria; a página de cliente
 * troca por aba, e é por isso que ela pode definir a chave ela mesma.
 */
const DefinirAjuda = createContext<(chave: ChaveDeAjuda | null) => void>(() => undefined);

export const ProvedorDeAjuda = DefinirAjuda.Provider;

export function AjudaDaTela({ chave }: { readonly chave: ChaveDeAjuda }): null {
  const definir = useContext(DefinirAjuda);
  useEffect(() => {
    definir(chave);
    return () => definir(null);
  }, [chave, definir]);
  return null;
}

export function chaveDaRota(caminho: string): ChaveDeAjuda | null {
  if (caminho === '/') return 'painel';
  if (caminho.startsWith('/inbox')) return 'conversas';
  if (caminho.startsWith('/clientes/')) return 'cliente_geral';
  if (caminho.startsWith('/clientes')) return 'clientes';
  if (caminho.startsWith('/agenda')) return 'agenda';
  if (caminho.startsWith('/equipe')) return 'equipe';
  if (caminho.startsWith('/configuracoes')) return 'configuracoes';
  return null;
}

/** O botão da topbar e o mini tutorial da tela, em seções curtas com exemplo. */
export function BotaoDeAjuda({ chave }: { readonly chave: ChaveDeAjuda }): ReactElement {
  const a = useT().ajuda;
  const [aberta, setAberta] = useState(false);
  const tela = a.telas[chave];

  return (
    <>
      <button
        type="button"
        className="ajuda__botao"
        onClick={() => setAberta(true)}
        aria-haspopup="dialog"
      >
        <span className="ajuda__icone" aria-hidden="true">
          ?
        </span>
        {a.botao}
      </button>

      {aberta ? (
        <Modal
          wide
          title={tela.titulo}
          subtitle={tela.resumo}
          onClose={() => setAberta(false)}
          footer={
            <button type="button" className="btn btn--primary" onClick={() => setAberta(false)}>
              {a.entendi}
            </button>
          }
        >
          <div className="ajuda">
            {tela.secoes.map((s) => (
              <section key={s.titulo} className="ajuda__secao">
                <h3>{s.titulo}</h3>
                <ul>
                  {s.itens.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}
            {tela.dica === undefined ? null : <p className="ajuda__dica">{tela.dica}</p>}
          </div>
        </Modal>
      ) : null}
    </>
  );
}
